import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { CategoryProcess as View } from '@views';
import { LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import { routes } from '@constants';
import {
  getCategoryItems, getCategorySubcategories, getCategorySizes, getCategorySpecials,
} from '@helpers/categoryEdit';
import { isPriceValid, formatPrice } from '@helpers/itemEdit';
import { formatPrice as formatDiscountAmount } from '@helpers/menuItemDetails';
import { isClientError } from '@helpers/errorHandling';
import { getEntityAvailabilities, getNumberFromString, hasSizesWithNullablePrice } from '@helpers/common';
import {
  editCategory, deleteCategory, createCategory, detachAvailabilityFromCategory, editAvailability, deleteAvailability,
  editSpecialCategory, createSpecialCategory, deleteSpecialCategory, detachAvailabilityFromSpecialCategory,
} from '@store/menu/duck';

const CategoryProcess = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    categories, items, sizes, subcategories, editCategoryPhase, deleteCategoryPhase, createCategoryPhase,
    availabilities, detachAvailabilityFromCategoryPhase, detachAvailabilityFromCategoryError, deleteAvailabilityPhase,
    editAvailabilityPhase, specialsCategory, editSpecialCategoryPhase, createSpecialCategoryPhase,
    deleteSpecialCategoryPhase, detachAvailabilityFromSpecialCategoryPhase, detachAvailabilityFromSpecialCategoryError,
  } = useSelector(state => state.menu);

  const categoryId = location.state ? location.state.id : null;

  const memoizedItems = useMemo(() => getCategoryItems(categories, categoryId, items), [categoryId]);
  const memoizedSubcategories = useMemo(() => (
    getCategorySubcategories(subcategories, categories, categoryId)
  ), [categoryId]);
  const defaultName = categoryId && categories.sortedIds.includes(categoryId)
    ? categories.byId[categoryId].name
    : '';
  const defaultImage = categoryId && categories.sortedIds.includes(categoryId)
    ? categories.byId[categoryId].image
    : '';
  const defaultActiveState = categoryId && categories.sortedIds.includes(categoryId)
    ? categories.byId[categoryId].active
    : true;
  const memoizedAvailabilities = useMemo(() => (
    getEntityAvailabilities(categories.byId[categoryId]?.availabilityIds, availabilities)
  ), [availabilities, categories]);

  // <-------- Category --------->//
  const [categoryItems, setCategoryItems] = useState(memoizedItems);
  const [categorySubcategories, setCategorySubcategories] = useState(memoizedSubcategories);
  const [categoryName, setCategoryName] = useState(defaultName);
  const [isCategoryActive, setIsCategoryActive] = useState(defaultActiveState);
  const [imageFile, setImageFile] = useState(null);
  const [image, setImage] = useState(defaultImage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasServerError, setHasServerError] = useState(false);
  const [error, setError] = useState(null);
  const [availabilityList, setAvailabilityList] = useState(memoizedAvailabilities);

  const itemsOrder = categoryItems.map(item => item.id);
  const subcategoriesOrder = categorySubcategories.map(subcategory => subcategory.id);

  // Check if category name !== '', and if something has changed (we don't need to edit category if info is the same))
  const isConfirmButtonDisabled = !categoryName;

  const isDataTheSame = (!!categoryId
      && categories.sortedIds.includes(categoryId) && image === categories.byId[categoryId].image)
    && (categoryName === categories.byId[categoryId].name)
    && (isCategoryActive === categories.byId[categoryId].active)
    && (JSON.stringify(itemsOrder) === JSON.stringify(categories.byId[categoryId].itemIds))
    && (JSON.stringify(subcategoriesOrder) === JSON.stringify(categories.byId[categoryId].subcategoryIds));

  // <-------- Category availability --------->//
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [availabilityId, setAvailabilityId] = useState(null);
  const [availabilityName, setAvailabilityName] = useState('');
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isAvailabilityDeleteModalOpen, setIsAvailabilityDeleteModalOpen] = useState(false);

  // Check if something has changed (we don't need to edit availability if info is the same)
  const isAvailabilityDataTheSame = (!!availabilityId && !!availabilities[availabilityId]
    && startDate
    && ((!endDate && !availabilities[availabilityId].endDate) || endDate)
    && startTime
    && endTime
    && (availabilities[availabilityId].name === availabilityName)
    && (availabilities[availabilityId].startDate === startDate.toISOString())
    && availabilities[availabilityId].startTime === startTime.toISOString()
    && ((availabilities[availabilityId].endDate && availabilities[availabilityId].endDate === endDate.toISOString())
    || (!availabilities[availabilityId].endDate && !endDate))
    && availabilities[availabilityId].endTime === endTime.toISOString()
    && JSON.stringify(selectedWeekDays) === JSON.stringify(availabilities[availabilityId].weekDays));

  const isAvailabilityConfirmButtonDisabled = !availabilityName
  || !selectedWeekDays.length
  || !startTime
  || !endTime
  || !startDate
  || new Date(startTime.setDate(1)).toISOString() > new Date(endTime.setDate(1)).toISOString()
  // set date to have the same date and compare only time
  || (!!endDate && new Date(startDate).toISOString() > new Date(endDate).toISOString());

  // <-------- Category specials --------->//
  const [specialId, setSpecialId] = useState(null);

  const memoizedSpecials = useMemo(() => (
    getCategorySpecials(specialsCategory, categories, categoryId)
  ), [categoryId, categories, specialsCategory]);

  const memoizedSpecialAvailabilities = useMemo(() => (
    getEntityAvailabilities(specialsCategory[specialId]?.availabilityIds, availabilities)
  ), [availabilities, specialsCategory, specialId]);

  const [specials, setSpecials] = useState(memoizedSpecials);
  const [isSizesSpecialModalOpen, setIsSizesSpecialModalOpen] = useState(false);
  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
  const [discountType, setDiscountType] = useState('price');
  const [price, setPrice] = useState('0');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [discountPercent, setDiscountPercent] = useState('0');
  const [selectedSizeSpecial, setSelectedSizeSpecial] = useState(null);
  const [isSpecialDeleteModalOpen, setIsSpecialDeleteModalOpen] = useState(false);
  const [isSpecialWarningModalOpen, setIsSpecialWarningModalOpen] = useState(false);

  const memoizedCategorySizes = useMemo(() => (
    getCategorySizes(categoryId, categories, items, sizes)
  ), [categoryId, categories, sizes, items]);

  const handleSpecialModalChangeState = () => {
    setSpecialId(null);
    setPrice(0);
    setDiscountAmount(0);
    setDiscountPercent(0);
    setDiscountType('price');
    setIsSpecialModalOpen(prevState => !prevState);
  };

  useEffect(() => {
    if (deleteCategoryPhase === SUCCESS
      || editCategoryPhase === SUCCESS) {
      history.push(routes.menu);
    }
    if (deleteCategoryPhase === FAILURE
      || editCategoryPhase === FAILURE) {
      setHasServerError(true);
    }
  }, [deleteCategoryPhase, editCategoryPhase]);

  useEffect(() => {
    if (createCategoryPhase === SUCCESS) {
      history.push({
        pathname: routes.menu,
        state: {
          categoryCreated: true,
        },
      });
    }
    if (createCategoryPhase === FAILURE) {
      setHasServerError(true);
    }
  }, [createCategoryPhase]);

  useEffect(() => {
    if (detachAvailabilityFromCategoryPhase === SUCCESS) {
      setAvailabilityList(getEntityAvailabilities(categories.byId[categoryId]?.availabilityIds, availabilities));
    }
    if (detachAvailabilityFromCategoryPhase === FAILURE) {
      if (isClientError(detachAvailabilityFromCategoryError && detachAvailabilityFromCategoryError.data.status)) {
        setError(detachAvailabilityFromCategoryError.data.error);
      }
    }
  }, [detachAvailabilityFromCategoryPhase]);

  useEffect(() => {
    if (detachAvailabilityFromSpecialCategoryPhase === FAILURE) {
      if (isClientError(detachAvailabilityFromSpecialCategoryError
         && detachAvailabilityFromSpecialCategoryError.data.status)) {
        setError(detachAvailabilityFromSpecialCategoryError.data.error);
      }
    }
  }, [detachAvailabilityFromSpecialCategoryPhase]);

  useEffect(() => {
    if (editAvailabilityPhase === SUCCESS
      || deleteAvailabilityPhase === SUCCESS) {
      setAvailabilityList(getEntityAvailabilities(categories.byId[categoryId]?.availabilityIds, availabilities));
      setIsAvailabilityDeleteModalOpen(false);
      setIsAvailabilityModalOpen(false);
    }
    if (editAvailabilityPhase === FAILURE
      || deleteAvailabilityPhase === FAILURE) {
      setIsAvailabilityModalOpen(false);
    }
  }, [deleteAvailabilityPhase, editAvailabilityPhase]);

  useEffect(() => {
    if (editSpecialCategoryPhase === SUCCESS
      || createSpecialCategoryPhase === SUCCESS
      || deleteSpecialCategoryPhase === SUCCESS) {
      setSpecials(getCategorySpecials(specialsCategory, categories, categoryId));
      handleSpecialModalChangeState();
      setIsSpecialDeleteModalOpen(false);
      setIsSpecialWarningModalOpen(false);
    }
    if (editSpecialCategoryPhase === FAILURE
      || createSpecialCategoryPhase === FAILURE
      || deleteSpecialCategoryPhase === FAILURE) {
      handleSpecialModalChangeState();
      setIsSpecialDeleteModalOpen(false);
      setIsSpecialWarningModalOpen(false);
    }
  }, [editSpecialCategoryPhase, createSpecialCategoryPhase, deleteSpecialCategoryPhase]);

  // <---------- Category edit/create/delete actions ----------> //

  const handleToastClose = () => {
    setHasServerError(false);
  };

  const handleModalChangeState = () => {
    setIsModalOpen(prevState => !prevState);
  };

  const handleEditCancel = () => {
    history.push(routes.menu);
  };

  const handleImageChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(URL.createObjectURL(e.target.files[0]));
      setImageFile(e.target.files[0]);
    }
  };

  const handleCategoryActivityChange = () => {
    setIsCategoryActive(prevState => !prevState);
  };

  const handleDragSubcategoryEnd = ({ destination, source }) => {
    if (destination && destination.index !== source.index) {
      const reorderedSubcategories = [...categorySubcategories];
      reorderedSubcategories.splice(source.index, 1);
      reorderedSubcategories.splice(destination.index, 0, categorySubcategories[source.index]);
      setCategorySubcategories(reorderedSubcategories);
    }
  };

  const handleDragItemEnd = ({ destination, source }) => {
    if (destination && destination.index !== source.index) {
      const reorderedItems = [...categoryItems];
      reorderedItems.splice(source.index, 1);
      reorderedItems.splice(destination.index, 0, categoryItems[source.index]);
      setCategoryItems(reorderedItems);
    }
  };

  const handleCategoryNameChange = e => {
    setCategoryName(e.target.value);
  };

  const handleCategoryDelete = () => {
    dispatch(deleteCategory({
      id: parseInt(categoryId, 10),
    }));
  };

  const handleEditSubmit = () => {
    if (isDataTheSame) {
      handleEditCancel();
    } else {
      const categoryData = new FormData();

      if ((!categoryId && image) || (categoryId && image !== categories.byId[categoryId].image)) {
        categoryData.append('image', imageFile);
      }

      if (!categoryId || (categoryId && categoryName !== categories.byId[categoryId].name)) {
        categoryData.append('name', categoryName);
      }

      if (categoryId && (!subcategoriesOrder.length
        && JSON.stringify(itemsOrder) !== JSON.stringify(categories.byId[categoryId].itemIds))) {
        categoryData.append('itemsOrder', JSON.stringify(itemsOrder));
      }

      if (categoryId && (subcategoriesOrder
        && JSON.stringify(subcategoriesOrder) !== JSON.stringify(categories.byId[categoryId].subcategoryIds))) {
        categoryData.append('subcategoriesOrder', JSON.stringify(subcategoriesOrder));
      }

      if (!categoryId || (categoryId && isCategoryActive !== categories.byId[categoryId].active)) {
        categoryData.append('active', isCategoryActive);
      }

      if (categoryId) {
        categoryData.append('id', categoryId);

        dispatch(editCategory(
          categoryData,
        ));
      } else {
        dispatch(createCategory(
          categoryData,
        ));
      }
    }
  };

  // <---------- Category availability actions ----------> //

  const handleAvailabilityAdd = () => {
    history.push({
      pathname: routes.availabilitiesList,
      state: {
        id: categoryId,
        appliedIds: categories.byId[categoryId]?.availabilityIds,
        entity: 'category',
      },
    });
  };

  const handleAvailabilityDetach = id => () => {
    dispatch(detachAvailabilityFromCategory({
      availabilityId: parseInt(id, 10),
      categoryId: parseInt(categoryId, 10),
    }));
  };

  const handleDayClick = dayId => () => {
    const days = [...selectedWeekDays];
    if (days.includes(dayId)) {
      const index = days.indexOf(dayId);

      if (index > -1) {
        days.splice(index, 1);
      }
    } else {
      days.push(dayId);
    }
    setSelectedWeekDays(days);
  };

  const handleAvailabilityDeleteModalChangeState = () => {
    setIsAvailabilityDeleteModalOpen(prevState => !prevState);
  };

  const handleAvailabilityNameChange = e => {
    setAvailabilityName(e.target.value);
  };

  const handleAvailabilityModalChangeState = () => {
    setAvailabilityId(null);
    setAvailabilityName('');
    setStartTime('');
    setEndTime('');
    setStartDate('');
    setEndDate('');
    setSelectedWeekDays([]);
    setIsAvailabilityModalOpen(prevState => !prevState);
  };

  const handleStartTimeChange = time => {
    setStartTime(time);
  };

  const handleEndTimeChange = time => {
    setEndTime(time);
  };

  const handleStartDateChange = date => {
    setStartDate(date);
  };

  const handleEndDateChange = date => {
    setEndDate(date);
  };

  const handleAvailabilityEdit = editingId => () => {
    setIsAvailabilityModalOpen(true);
    setAvailabilityId(editingId);
    setAvailabilityName(availabilities[editingId].name);
    setStartTime(new Date(availabilities[editingId].startTime));
    setEndTime(new Date(availabilities[editingId].endTime));
    setStartDate(new Date(availabilities[editingId].startDate));
    setEndDate(availabilities[editingId].endDate ? new Date(availabilities[editingId].endDate) : '');
    setSelectedWeekDays(availabilities[editingId].weekDays);
  };

  const handleAvailabilityDelete = () => {
    dispatch(deleteAvailability({
      id: parseInt(availabilityId, 10),
    }));
  };

  const handleStartDateRemove = () => {
    setStartDate('');
  };

  const handleEndDateRemove = () => {
    setEndDate('');
  };

  const handleStartTimeRemove = () => {
    setStartTime('');
  };

  const handleEndTimeRemove = () => {
    setEndTime('');
  };

  const handleAvailabilityEditSubmit = () => {
    if (isAvailabilityDataTheSame) {
      setIsAvailabilityModalOpen(false);
    } else {
      const availability = {};
      if ((availabilityName && !availabilityId)
        || (availabilityName && availabilityId && availabilityName !== availabilities[availabilityId].name)) {
        availability.name = availabilityName;
      }
      if ((selectedWeekDays.length && !availabilityId)
        || (selectedWeekDays.length && availabilityId
          && JSON.stringify(selectedWeekDays) !== JSON.stringify(availabilities[availabilityId].weekDays))) {
        availability.weekDays = selectedWeekDays;
      }
      if (startDate) {
        availability.startDate = startDate.toISOString();
      }
      if (endDate) {
        availability.endDate = endDate.toISOString();
      }
      if (!endDate && !!availabilities[availabilityId]?.endDate) {
        availability.endDate = null;
      }
      if (startTime) {
        availability.startTime = startTime.toISOString();
      }
      if (endTime) {
        availability.endTime = endTime.toISOString();
      }
      availability.id = parseInt(availabilityId, 10);

      dispatch(editAvailability(availability));
    }
  };

  // <---------- Category specials actions ----------> //

  const handleDiscountPercentChange = e => {
    let cash;
    if (!e.target.value.includes('%')) {
      cash = e.target.value.replace(',', '.').slice(0, -1);
    } else {
      cash = e.target.value.replace(',', '.').replace('%', '');
    }
    if (isPriceValid(cash)) { // we check if entered value !== 0 to not replace whole value with empty string
      setDiscountPercent(formatPrice(parseInt(cash, 10) !== 0 && parseInt(discountPercent, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handlePriceChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered price !== 0 to not replace whole value with empty string
      setPrice(formatPrice(parseInt(cash, 10) !== 0 && parseInt(price, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleDiscountAmountChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered value !== 0 to not replace whole value with empty string
      setDiscountAmount(formatPrice(parseInt(cash, 10) !== 0 && parseInt(discountAmount, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleDiscountTypeChange = typeId => () => {
    setDiscountType(typeId);
  };

  const discountTypes = {
    price: {
      name: 'PRICE',
      inputValue: `$${price}`,
      inputHandler: handlePriceChange,
    },
    discount_amount: {
      name: 'DISCOUNT AMOUNT',
      inputValue: `$${discountAmount}`,
      inputHandler: handleDiscountAmountChange,
    },
    discount_percent: {
      name: 'DISCOUNT PERCENT',
      inputValue: `${discountPercent}%`,
      inputHandler: handleDiscountPercentChange,
    },
  };

  const isSpecialDataTheSame = (!!specialId && !!specialsCategory[specialId]
    && (specialsCategory[specialId].discountType === discountType)
    && (specialsCategory[specialId].discountValue === getNumberFromString(discountTypes[discountType].inputValue))
  );

  const isSpecialConfirmButtonDisabled = !getNumberFromString(discountTypes[discountType].inputValue);

  const handleEditConfirm = () => {
    const specialCategory = {
      discountType,
      discountValue: getNumberFromString(discountTypes[discountType].inputValue),
    };

    if (specialId) {
      specialCategory.id = specialId;
      dispatch(editSpecialCategory(specialCategory));
    } else {
      specialCategory.categoryId = categoryId;
      specialCategory.sizeName = sizes[selectedSizeSpecial].name;
      dispatch(createSpecialCategory(specialCategory));
    }
  };

  const handleSpecialWarningModalClose = () => {
    setIsSpecialWarningModalOpen(false);
  };

  const handleSpecialEditSubmit = () => {
    if (isSpecialDataTheSame) {
      handleSpecialModalChangeState();
    } else if (hasSizesWithNullablePrice(memoizedCategorySizes, sizes, selectedSizeSpecial,
      discountType, discountTypes[discountType].inputValue)) {
      setIsSpecialWarningModalOpen(true);
    } else {
      handleEditConfirm();
    }
  };

  const handleSpecialDeleteModalChangeState = () => {
    setIsSpecialDeleteModalOpen(prevState => !prevState);
  };

  const handleSpecialDelete = () => {
    dispatch(deleteSpecialCategory({
      id: parseInt(specialId, 10),
    }));
  };

  const handleSelectedSizeSpecialChange = sizeId => () => {
    setSelectedSizeSpecial(sizeId);
    setIsSizesSpecialModalOpen(false);
    setIsSpecialModalOpen(true);
  };

  const setDiscountValue = id => {
    if (specialsCategory[id].discountType === 'price') {
      setPrice(formatDiscountAmount(specialsCategory[id].discountValue));
    }
    if (specialsCategory[id].discountType === 'discount_amount') {
      setDiscountAmount(formatDiscountAmount(specialsCategory[id].discountValue));
    }
    if (specialsCategory[id].discountType === 'discount_percent') {
      setDiscountPercent(formatDiscountAmount(specialsCategory[id].discountValue));
    }
  };

  const handleSpecialEdit = id => () => {
    setSpecialId(id);
    setSelectedSizeSpecial(memoizedCategorySizes.find(size => size.name === specialsCategory[id].sizeName).id);
    setDiscountType(specialsCategory[id].discountType);
    setDiscountValue(id);
    setIsSpecialModalOpen(prevState => !prevState);
  };

  const handleSizesSpecialModalChangeState = () => {
    setIsSizesSpecialModalOpen(prevState => !prevState);
  };

  const handleSpecialAdd = () => {
    setIsSizesSpecialModalOpen(true);
  };

  const handleSpecialAvailabilityDetach = id => () => {
    dispatch(detachAvailabilityFromSpecialCategory({
      availabilityId: parseInt(id, 10),
      specialCategoryId: parseInt(specialId, 10),
    }));
  };

  const handleSpecialAvailabilityAdd = () => {
    history.push({
      pathname: routes.availabilitiesList,
      state: {
        id: specialId,
        appliedIds: specialsCategory[specialId]?.availabilityIds,
        entity: 'specialCategory',
      },
    });
  };

  return (
    <View
      // specials
      onSpecialWarningModalClose={handleSpecialWarningModalClose}
      onEditConfirm={handleEditConfirm}
      isSpecialWarningModalOpen={isSpecialWarningModalOpen}
      onSpecialAvailabilityAdd={handleSpecialAvailabilityAdd}
      specialAvailabilities={memoizedSpecialAvailabilities}
      onSpecialAvailabilityDetach={handleSpecialAvailabilityDetach}
      isSpecialConfirmButtonDisabled={isSpecialConfirmButtonDisabled}
      onSpecialDeleteModalChangeState={handleSpecialDeleteModalChangeState}
      onSpecialDelete={handleSpecialDelete}
      isSpecialDeleteModalOpen={isSpecialDeleteModalOpen}
      onSpecialEditSubmit={handleSpecialEditSubmit}
      onSpecialAdd={handleSpecialAdd}
      isSizesSpecialModalOpen={isSizesSpecialModalOpen}
      onSizesSpecialModalChangeState={handleSizesSpecialModalChangeState}
      onSelectedSizeSpecialChange={handleSelectedSizeSpecialChange}
      selectedSizeSpecial={selectedSizeSpecial}
      categorySizes={memoizedCategorySizes}
      isSpecialEditing={!!specialId}
      onSpecialEdit={handleSpecialEdit}
      categorySpecials={specials}
      discountTypes={discountTypes}
      onSpecialModalChangeState={handleSpecialModalChangeState}
      isSpecialModalOpen={isSpecialModalOpen}
      onDiscountTypeChange={handleDiscountTypeChange}
      selectedDiscountType={discountType}
      // category availability
      onStartDateRemove={handleStartDateRemove}
      onEndDateRemove={handleEndDateRemove}
      onStartTimeRemove={handleStartTimeRemove}
      onEndTimeRemove={handleEndTimeRemove}
      onAvailabilityEdit={handleAvailabilityEdit}
      isAvailabilityConfirmButtonDisabled={isAvailabilityConfirmButtonDisabled}
      onAvailabilityModalChangeState={handleAvailabilityModalChangeState}
      isAvailabilityModalOpen={isAvailabilityModalOpen}
      onAvailabilityDelete={handleAvailabilityDelete}
      onAvailabilityDeleteModalChangeState={handleAvailabilityDeleteModalChangeState}
      onAvailabilityEditSubmit={handleAvailabilityEditSubmit}
      isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
      isAvailabilityEditing={!!availabilityId}
      startTime={startTime.toString()}
      endTime={endTime.toString()}
      startDate={startDate.toString()}
      endDate={endDate.toString()}
      onStartTimeChange={handleStartTimeChange}
      onEndTimeChange={handleEndTimeChange}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      onDayClick={handleDayClick}
      selectedWeekDays={selectedWeekDays}
      onAvailabilityNameChange={handleAvailabilityNameChange}
      availabilityName={availabilityName}
      onAvailabilityDetach={handleAvailabilityDetach}
      availabilities={availabilityList}
      onAvailabilityAdd={handleAvailabilityAdd}
      // category
      isCategoryActive={isCategoryActive}
      onCategoryActivityChange={handleCategoryActivityChange}
      onModalChangeState={handleModalChangeState}
      isModalOpen={isModalOpen}
      isEditing={!!categoryId}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      onCategoryDelete={handleCategoryDelete}
      onDragSubcategoryEnd={handleDragSubcategoryEnd}
      onDragItemEnd={handleDragItemEnd}
      onImageChange={handleImageChange}
      image={image}
      onEditCancel={handleEditCancel}
      items={categoryItems}
      subcategories={categorySubcategories}
      categoryName={categoryName}
      onCategoryNameChange={handleCategoryNameChange}
      onEditSubmit={handleEditSubmit}
      onToastClose={handleToastClose}
      hasServerError={hasServerError}
      error={error}
      isLoading={editCategoryPhase === LOADING
        || deleteCategoryPhase === LOADING
        || createCategoryPhase === LOADING
        || detachAvailabilityFromCategoryPhase === LOADING
        || deleteAvailabilityPhase === LOADING
        || editAvailabilityPhase === LOADING
        || editSpecialCategoryPhase === LOADING
        || createSpecialCategoryPhase === LOADING
        || deleteSpecialCategoryPhase === LOADING
        || detachAvailabilityFromSpecialCategoryPhase === LOADING}
    />
  );
};

export default CategoryProcess;
